import { C } from '@angular/cdk/keycodes';
import { Injectable, isDevMode } from '@angular/core';
import { throwToolbarMixedModesError } from '@angular/material/toolbar';
import { TranslateService } from '@ngx-translate/core';
import { use } from 'vue/types/umd';
import { PeopleService } from '../../../app/users/+users/see-users/shared/services/people.service';
import { API_URLS } from '../../../environments/env-routes';
import { User } from '../../models/user.class';
import { APIHelper } from '../api-helper/api-helper.service';
import { StorageService } from '../storage/storage.service';
import { V2nodeApiService } from '../v2node-api.service';

interface LoginParameters {
  pseudo: string;
  pass: string;
}

interface LoginTokenResponse {
  id: string;
  userId: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  public lastError: string;
  public user: User;
  public actualRoleSite: any;
  public odlpwd: string;
  public favorites: any[];
  public isAdmin: boolean;
  public isCommercial: boolean;
  public isExternal: boolean;
  public pendingWhoAmI: Promise<any>;

  constructor(
    public peopleService: PeopleService,
    private api: APIHelper,
    private storage: StorageService,
    private v2node: V2nodeApiService,
    private translate: TranslateService
  ) {
    this.initialize();
  }

  private initialize() {
    this.user = null;
    this.actualRoleSite = {
      isConsumer: false,
      isContributor: false,
      isCollaborator: false,
      isManager: false
    };
  }

  get token() {
    const token = localStorage.getItem('token');
    return token;
  }

  set token(value) {
    localStorage.setItem('token', value);
  }

  async getCurrentUser() {
    return await this.connectedUser();
  }

  async isLoggedIn(): Promise<boolean> {
    return await this.isTokenValid();
  }

  public async login({ pseudo, pass }: LoginParameters): Promise<boolean> {
    const body = {
      userId: pseudo,
      password: pass
    };
    this.odlpwd = body.password;
    const coeff = isDevMode() ? 1 : 1;
    const datePeremption = +new Date() + (4 * 3600 * 1000) * coeff;
    try {
      const tokenRes: LoginTokenResponse = await this.api.requestApi({ action: API_URLS.user.login, method: 'POST', datas: body });
      const token = tokenRes.id;

      await Promise.all([
        this.storage.set({ key: 'token', value: token }),
        this.storage.set({ key: 'bearer', value: btoa(`${pseudo}:${pass}`) }),
        this.storage.set({ key: 'validity', value: datePeremption.toString() })
      ]);

      this.user = await this.whoAmI();

      return true;
    } catch (e) {
     
      switch (e.status) {
        case 403:
          this.lastError = 'Invalid login info';
          break;
        case 504:
          this.lastError = 'No communication with Alfresco API';
          break;
      }
      throw e;
    }
  }

  public async loadSearchFavorites() {
    this.favorites = await this.v2node.requestApi({ action: '/search/favorites' });
  }

  public async addFavorite(term) {
    const datas = { searchText: term };
   
    return await this.v2node.requestApi({ action: '/search/favorites', method: 'POST', datas });
  }

  public async removeFavorite(favoriteId) {
    return await this.v2node.requestApi({ action: `/search/favorites/${favoriteId}`, method: 'DELETE' });
  }

  public async getSearchHistory(page, nbElements) {
    return await this.v2node.requestApi({ action: `/search/history`, queryParams: { page, nbElements } });
  }

  public async removeHistory(historyId) {
    return await this.v2node.requestApi({ action: `/search/history/${historyId}`, method: 'DELETE' });
  }

  public async clearHistory() {
    return await this.v2node.requestApi({ action: `/search/history`, method: 'DELETE' });
  }

  public async isTokenValid() {
    if (this.token) {
      try {
        await this.api.requestApi({ action: API_URLS.user.tokenValidation });
        const actualdate = +new Date();
        const validity = this.storage.getAsync({ key: 'validity' });
        if (parseInt(validity) <= actualdate) {
          return false;

        } else {
          return true;
        }
       

      } catch (e) {
        if (e.status === 404) {
          this.lastError = 'Votre session a expirée';
        }
        if (e.status === 504) {
          this.lastError = 'Communication avec le serveur perdue';

        }
       
        return false;
      }
    }
    return false;
  }
  public async checkIfIsExternal(id: string) {
    return this.v2node.requestApi({ action: '/consultant/sheet/isExternal/' + id }).then(res => {
      this.user.isExternal = res;
    })
  }


  public async whoAmI(): Promise<User> {
    if (this.user) {
      return this.user;
    }
    try {
      const userResponse = await this.api.requestApi({ action: API_URLS.user.whoAmI });
      const userFromPeopleApi = await this.peopleService.getOnePeople(userResponse.id);
      const user = new User(userResponse);
      user.nodeRef = userFromPeopleApi.nodeRef;
      const userGroups = await this.api.requestApi({ action: API_URLS.user.getMyGroups });
      user.groups = userGroups.list.entries.map(e => e.entry);


      user.groups.forEach(group => {
        if (group.id == "GROUP_Commerciaux") {
          this.isCommercial = true;
          user.isCommercial = true;
        }
        if (group.id === 'GROUP_Clients') {
          user.isClient = true;
        }
        if (group.id === 'GROUP_Collaborateurs') {
          user.isConsultant = true;
        }

      })


      user.groups.forEach(g => {
        //if (g.id === 'GROUP_Managers') {
        //  user.isManager = true;
        //} else 


      });

      this.user = user;
      try {

        await this.checkIfIsExternal(this.user.email);

      } catch (e) {

        this.user.isExternal = false;
      }


      return user;
    } catch (e) {
    
      return null;
    }
  }

  public connectedUser(): Promise<User> {
    try {
      if (this.user) {
        return new Promise(resolve => resolve(this.user));
      } else {
        return this.whoAmI();
      }
    } catch (e) {
    
    }
  }


  public updatePassword(email) {
    const res = this.api.requestApi({
      action: API_URLS.user.resetPass,
      datas: { email: email },
      method: 'post'
    });

    return res;
  }

  public transformEmailUsername(email) {
    const res = this.api.requestApi({
      action: API_URLS.user.getemailusername,
      datas: {
        email: email
      },
      method: 'get'
    });

    return res;
  }

  public updatePass(mdp, key, activiti, username) {
    const res = this.api.requestApi({
      action: API_URLS.user.updatePass,
      datas: {
        password: mdp,
        key: key,
        activiti: activiti,
        username: username
      },
      method: 'post'
    });

    return res;
  }

  public async logout() {
    try {
      const token = this.token;

      await this.api.requestApi({ action: API_URLS.user.logout.replace(':ticket', token), method: 'DELETE' });

      this._clearLoginInofrmation();
      return false;
    } catch (e) {
      this._clearLoginInofrmation();
      return false;
    }
  }

  public loadAllUsers(): Promise<User[]> {
    return this.api.requestApi({ action: API_URLS.user.getAll }).then(res => {
      return res.list.entries.map(us => new User(us.entry));
    });
  }

  public setRoleSite(role: string) {
    if (role === 'SiteCollaborator') {
      this.actualRoleSite.isCollaborator = true;
    } else if (role === 'SiteConsumer') {
      this.actualRoleSite.isConsumer = true;
    } else if (role === 'SiteContributor') {
      this.actualRoleSite.isContributor = true;
    } else if (role === 'SiteManager') {
      this.actualRoleSite.isManager = true;
    }
  }

  private _clearLoginInofrmation() {
    // localStorage.removeItem('token');
    // localStorage.removeItem('bearer');
    // localStorage.removeItem('validity');
    localStorage.clear();
    this.initialize();
  }


}
